<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, onUpdated, ref, watch } from "vue";
import { createCompletedOrder, createEventMutation, discountTypeMinorFor, type CartLine, type CurrencyCode, type DiscountType, type LocalPrintJob, type MenuItem, type Order, type OrderEvent, type PaymentMethod, type RefundMethod, type SelectedOption, type SyncConflict } from "@shared/contracts";
import { IndexedDbRecordStore, PosLocalRepository } from "./lib/pos-repository";
import { SyncClient, type SyncIdentity } from "./lib/sync-client";
import { DeviceConfigSyncClient } from "./lib/device-config-sync";
import { DualStreamSyncClient } from "./lib/dual-sync-client";
import { SyncCoordinator, type SyncCoordinatorSnapshot } from "./lib/sync-coordinator";
import { hasPermission, type Permission, type Role } from "./lib/access";
import { LocalPrintBridgeAdapter, processPendingPrintJobs, WebSerialPrintAdapter } from "./lib/printing";
import { createPrintJobs, createReprintJob, evaluateHardware, hardwareTransportPath, validateNamedPrinterDeviceProfile, type BridgeLifecycleStatus, type HardwareStatus, type NamedPrinterDeviceProfile, type PrintRouteConfig } from "./lib/hardware";
import type { PrintCopiesMode } from "@shared/contracts";
import { ManualCardPaymentAdapter } from "./lib/manual-card";
import { businessDateFor, DEFAULT_BUSINESS_DAY_CONFIG, type BusinessDayConfig } from "./lib/business-day";
import type { DeviceConfigSnapshot } from "@shared/admin";
import type { AuthSession, DeviceBootstrapStoreSelection, OperatorDeviceContext } from "@shared/identity";
import { AuthClient } from "./lib/auth-client";
import { createLocalOperatorUnlock, findDeviceOperatorByPin, parseLocalOperatorUnlock, verifyLocalOperatorPin, type LocalOperatorUnlock } from "./lib/operator-unlock";
import { ensureSharedOperatorClientId, readOperatorDeviceContext, storeOperatorDeviceContext } from "./lib/operator-device";
import { languageTag, PWA_UPDATE_EVENT, requestPwaUpdate } from "./lib/pwa";
import { readLocalStorage, writeLocalStorage } from "./lib/storage";
import { translate, translateDocument } from "./lib/i18n";
import { minutesToPosTime, posDateBoundaryIso, posDateRangeAdapter, posTimeToMinutes, posTimeAdapter, type PosDateRangeValue, type PosTimeValue } from "./lib/date-picker";
import { selectedOptionsForMenuItem } from "@shared/menu";
import type { AdminOrderQuery } from "@shared/admin";
import { fetchRemoteOperatorOrderDetail, fetchRemoteOperatorOrders } from "./lib/operator-api";
import { toggleFullscreen } from "./lib/fullscreen";
import { BaseAlert, BaseAutocomplete, BaseButton, BaseInput, BasePagination, BasePopoverButton, BaseTable, type BaseSelectOption, type PaginationChangeEvent, type TableColumn } from "@lunagoai/vue-ui";
import { createRegionOptions } from "@lunagoai/locale-data";
import { createTimezoneOptions } from "@lunagoai/timezone";
import BaseCheckbox from "./components/ui/BaseCheckbox.vue";
import AppLoadingOverlay from "./components/AppLoadingOverlay.vue";
import PosSelect from "./components/PosSelect.vue";
import PwaUpdateDialog from "./components/PwaUpdateDialog.vue";
import BaseDatePicker from "@lunagoai/vue-ui/components/BaseDatePicker.vue";
import { createCurrencyOptions } from "@lunagoai/locale-data";

type View = "pos" | "orders" | "dashboard" | "settings";
type InitialOrderHistoryRange = "7" | "30" | "90" | "all";
type LocalOrderTableRow = Record<string, unknown> & {
  id: string;
  orderNo: string;
  createdAt: string;
  paymentMethod: PaymentMethod;
  totalMinor: number;
  status: Order["status"];
  note: string;
  sourceOrder: Order;
};
type LocalSettings = {
  autoSync: boolean;
  syncIntervalSeconds: number;
  language: "en" | "zh-Hant" | "zh-Hans";
  theme: "light" | "dark" | "system";
  printTransport: "none" | "web-serial" | "local-bridge" | "bluetooth" | "usb";
  receiptTransport: "none" | "web-serial" | "local-bridge" | "bluetooth" | "usb";
  kitchenTransport: "none" | "web-serial" | "local-bridge" | "bluetooth" | "usb";
  bridgeEndpoint: string;
  paperWidth: 58 | 80;
  printCopies: PrintCopiesMode;
  printerProfiles: NamedPrinterDeviceProfile[];
};

const view = ref<View>("pos");
const online = ref(typeof navigator === "undefined" ? true : navigator.onLine);
const shellLoading = ref(true);
const loading = ref(true);
const busy = ref(false);
const menu = ref<MenuItem[]>([]);
const orders = ref<Order[]>([]);
const orderHistoryQuery = ref("");
const orderHistoryStatus = ref<"all" | "completed" | "voided">("all");
const orderHistoryPayment = ref<PaymentMethod | "all">("all");
const orderHistoryOrderNo = ref("");
const orderHistoryItemName = ref("");
const localOrderPage = ref(1);
const localOrderPageSize = ref(10);
const orderHistoryMinAmount = ref<number | null>(null);
const orderHistoryMaxAmount = ref<number | null>(null);
const orderHistoryFrom = ref("");
const orderHistoryTo = ref("");
const selectedOrder = ref<Order | null>(null);
const selectedOrderEvents = ref<OrderEvent[]>([]);
const selectedOrderLoading = ref(false);
const pendingCount = ref(0);
const conflictCount = ref(0);
const conflicts = ref<SyncConflict[]>([]);
const remoteHistoryOffset = ref(0);
const remoteHistoryHasMore = ref(false);
const remoteHistoryLoading = ref(false);
const remoteHistoryLoaded = ref(false);
const localStateLoaded = ref(false);
const printPendingCount = ref(0);
const syncState = ref("Local only");
const lastSyncAt = ref<string | null>(readLocalStorage("posnext.last-sync-at"));
const cart = ref<CartLine[]>([]);
const paymentMethod = ref<PaymentMethod>("cash");
const discountMinor = ref(0);
const discountType = ref<DiscountType>("none");
const discountReason = ref("");
const orderNote = ref("");
const cashReceivedMinor = ref<number | null>(null);
const mixedCashMinor = ref(0);
const cardConfirmed = ref(false);
const cardReference = ref("");
const customizingItem = ref<MenuItem | null>(null);
const customOptions = ref<Record<string, string[]>>({});
const itemNote = ref("");
const activeCategory = ref("Main dish");
const message = ref("");
const errorMessage = ref("");
const currentRole = ref<Role>("operator");
const operatorSession = ref<AuthSession | null>(null);
const deviceContext = ref<OperatorDeviceContext | null>(readOperatorDeviceContext());
const deviceConfigSnapshot = ref<DeviceConfigSnapshot | null>(null);
const localOperatorUnlock = ref<LocalOperatorUnlock | null>(parseLocalOperatorUnlock(readLocalStorage("posnext.operator-unlock")));
const operatorUnlocked = ref(false);
const authBusy = ref(false);
const authError = ref("");
const operatorPin = ref("");
const ownerBootstrap = ref({ email: readLocalStorage("posnext.owner-email") ?? "", secret: "" });
const ownerBootstrapStep = ref<"credentials" | "store">("credentials");
const ownerBootstrapHistoryRange = ref<InitialOrderHistoryRange>("7");
const ownerBootstrapStores = ref<Array<{ storeId: string; name: string }>>([]);
const ownerBootstrapCanCreateStore = ref(false);
const ownerBootstrapSelection = ref({
  mode: "existing" as "existing" | "new",
  storeId: readLocalStorage("posnext.store-id") ?? "",
  name: "",
  region: "United Kingdom",
  timeZone: "Europe/London",
  currency: "GBP" as CurrencyCode,
  businessDayStartMinutes: 240,
  deviceLabel: "Operator browser",
});
const ownerBootstrapBusinessDayTime = ref<PosTimeValue>(minutesToPosTime(ownerBootstrapSelection.value.businessDayStartMinutes));
const ownerBootstrapRegionSearch = ref(ownerBootstrapSelection.value.region);
const ownerBootstrapTimezoneSearch = ref(ownerBootstrapSelection.value.timeZone);
const webSerialAvailable = ref(false);
const bridgeAvailable = ref(false);
const hardwareStatus = ref<HardwareStatus | null>(null);
const bridgeLifecycle = ref<BridgeLifecycleStatus | null>(null);
const fullscreen = ref(false);
const operatorPopoverOpen = ref(false);
const pwaUpdateVisible = ref(false);
const pwaUpdateBusy = ref(false);
const settings = ref<LocalSettings>({
  autoSync: true,
  syncIntervalSeconds: 60,
  language: "en",
  theme: "system",
  printTransport: "none",
  receiptTransport: "none",
  kitchenTransport: "none",
  bridgeEndpoint: "http://127.0.0.1:17890",
  paperWidth: 58,
  printCopies: "kitchen",
  printerProfiles: [],
});
let syncCoordinator: SyncCoordinator | undefined;
let sessionCheckTimer: number | undefined;
const manualCardPayment = new ManualCardPaymentAdapter();

const repository = new PosLocalRepository(new IndexedDbRecordStore());
const authClient = new AuthClient();

const operatorDisplayName = computed(() => operatorSession.value?.displayName ?? localOperatorUnlock.value?.displayName ?? "Operator");
const operatorAuthMode = computed(() => operatorSession.value ? "Server authenticated" : operatorUnlocked.value ? "Local PIN unlock" : "Locked");

async function applyUiLanguage(): Promise<void> {
  await nextTick();
  translateDocument(settings.value.language);
}

const categories = computed(() => [...new Set(menu.value.map((item) => item.category))]);
const t = (key: string): string => translate(settings.value.language, key);
const ownerBootstrapModeOptions = computed<BaseSelectOption[]>(() => [
  ...(ownerBootstrapStores.value.length ? [{ value: "existing", label: t("ui.useExistingStore") }] : []),
  ...(ownerBootstrapCanCreateStore.value ? [{ value: "new", label: t("ui.createNewStore") }] : []),
]);
const ownerBootstrapStoreOptions = computed<BaseSelectOption[]>(() => [
  { value: "", label: t("ui.selectStore") },
  ...ownerBootstrapStores.value.map((store) => ({ value: store.storeId, label: `${store.name} · ${store.storeId}` })),
]);
const ownerBootstrapHistoryRangeOptions = computed<BaseSelectOption[]>(() => [
  { value: "7", label: t("ui.lastSevenDays") },
  { value: "30", label: t("ui.lastThirtyDays") },
  { value: "90", label: t("ui.lastNinetyDays") },
  { value: "all", label: t("ui.allHistory") },
]);
const currencyOptions = computed<BaseSelectOption[]>(() => [
  ...createCurrencyOptions(settings.value.language).map((option) => ({ value: option.code, label: `${option.symbol} ${option.code} · ${option.name}` })),
]);
const bootstrapRegionOptions = computed(() => createRegionOptions(settings.value.language).map((option) => ({ id: option.code, name: `${option.name} · ${option.code}`, code: option.code, searchText: option.searchText })));
const bootstrapTimezoneOptions = computed(() => createTimezoneOptions({ locale: settings.value.language, additionalTimezones: [ownerBootstrapSelection.value.timeZone] }).map((option) => ({ id: option.value, name: `${option.label} · ${option.value}`, value: option.value, searchText: `${option.label} ${option.value}` })));
const discountOptions = computed<BaseSelectOption[]>(() => [
  { value: "none", label: t("ui.none") },
  { value: "10off", label: t("ui.tenOff") },
  { value: "20off", label: t("ui.twentyOff") },
]);
const orderStatusOptions = computed<BaseSelectOption[]>(() => [
  { value: "all", label: t("ui.all") },
  { value: "completed", label: t("ui.completed") },
  { value: "voided", label: t("ui.voided") },
]);
const paymentOptions = computed<BaseSelectOption[]>(() => [
  { value: "all", label: t("ui.all") },
  { value: "cash", label: t("ui.cash") },
  { value: "card", label: t("ui.card") },
  { value: "mixed", label: t("ui.mixed") },
]);
const languageOptions = computed<BaseSelectOption[]>(() => [
  { value: "en", label: t("ui.english") },
  { value: "zh-Hant", label: t("ui.traditionalChinese") },
  { value: "zh-Hans", label: t("ui.simplifiedChinese") },
]);
const themeOptions = computed<BaseSelectOption[]>(() => [
  { value: "system", label: t("ui.system") },
  { value: "light", label: t("ui.light") },
  { value: "dark", label: t("ui.dark") },
]);
const printTransportOptions = computed<BaseSelectOption[]>(() => [
  { value: "none", label: t("ui.notConfigured") },
  { value: "web-serial", label: t("ui.webSerial") },
  { value: "local-bridge", label: t("ui.windowsBridge") },
  { value: "bluetooth", label: t("ui.bridgeBluetooth") },
  { value: "usb", label: t("ui.bridgeUsb") },
]);
const routeTransportOptions = computed<BaseSelectOption[]>(() => [
  { value: "none", label: t("ui.disabled") },
  { value: "web-serial", label: t("ui.webSerial") },
  { value: "local-bridge", label: t("ui.localBridge") },
  { value: "bluetooth", label: t("ui.bridgeBluetooth") },
  { value: "usb", label: t("ui.bridgeUsb") },
]);
const paperWidthOptions = computed<BaseSelectOption[]>(() => [
  { value: 58, label: "58mm" },
  { value: 80, label: "80mm" },
]);
const printModeOptions = computed<BaseSelectOption[]>(() => [
  { value: "none", label: t("ui.noAutomaticPrint") },
  { value: "kitchen", label: t("ui.kitchenOnly") },
  { value: "both", label: t("ui.kitchenCustomerReceipt") },
]);
const navigationItems = computed<Array<[View, string]>>(() => {
  const items: Array<[View, string]> = [
    ["pos", t("nav.menuPos")],
    ["orders", t("nav.orderHistory")],
    ["dashboard", t("nav.dashboard")],
  ];
  if (can("settings.manage")) items.push(["settings", t("nav.settings")]);
  return items;
});
const visibleMenu = computed(() => menu.value.filter((item) => item.active && item.category === activeCategory.value));
const cartSubtotalMinor = computed(() => cart.value.reduce((total, line) => total + lineTotalMinor(line), 0));
const discountTypeMinor = computed(() => discountTypeMinorFor(cartSubtotalMinor.value, discountType.value));
const cartTotalMinor = computed(() => Math.max(0, cartSubtotalMinor.value - discountMinor.value - discountTypeMinor.value));
const changeMinor = computed(() => Math.max(0, (cashReceivedMinor.value ?? 0) - cashDueMinor.value));
const cashDueMinor = computed(() => paymentMethod.value === "mixed" ? mixedCashMinor.value : paymentMethod.value === "cash" ? cartTotalMinor.value : 0);
const cardDueMinor = computed(() => paymentMethod.value === "mixed" ? cartTotalMinor.value - mixedCashMinor.value : paymentMethod.value === "card" ? cartTotalMinor.value : 0);
const viewTitle = computed(() => ({
  pos: t("nav.menuPos"), orders: t("nav.orderHistory"), dashboard: t("nav.dashboard"), settings: t("nav.settings"),
}[view.value]));
const totalSalesMinor = computed(() => orders.value.filter((order) => order.status === "completed").reduce((total, order) => total + order.totalMinor, 0));
const locale = computed(() => settings.value.language === "zh-Hant" ? "zh-Hant" : settings.value.language === "zh-Hans" ? "zh-Hans" : "en-GB");
const orderHistoryDateRangeValue = computed<PosDateRangeValue>(() => [orderHistoryFrom.value || null, orderHistoryTo.value || null]);
const orderHistoryDatePickerTimeZone = computed(() => getBusinessDayConfig().timeZone);
const visibleOrders = computed(() => {
  const query = orderHistoryQuery.value.trim().toLowerCase();
  const datePickerTimeZone = orderHistoryDatePickerTimeZone.value;
  const from = orderHistoryFrom.value ? posDateBoundaryIso(orderHistoryFrom.value, datePickerTimeZone) : undefined;
  const to = orderHistoryTo.value ? posDateBoundaryIso(orderHistoryTo.value, datePickerTimeZone, true) : undefined;
  const fromMillis = from ? new Date(from).getTime() : null;
  const toMillis = to ? new Date(to).getTime() : null;
  return orders.value.filter((order) => {
    const createdAt = new Date(order.createdAt).getTime();
    if (fromMillis !== null && createdAt < fromMillis) return false;
    if (toMillis !== null && createdAt > toMillis) return false;
    if (orderHistoryStatus.value !== "all" && order.status !== orderHistoryStatus.value) return false;
    if (orderHistoryPayment.value !== "all" && order.paymentMethod !== orderHistoryPayment.value) return false;
    if (orderHistoryMinAmount.value !== null && order.totalMinor < orderHistoryMinAmount.value) return false;
    if (orderHistoryMaxAmount.value !== null && order.totalMinor > orderHistoryMaxAmount.value) return false;
    if (orderHistoryOrderNo.value.trim() && !(order.orderNo ?? order.id).toLowerCase().includes(orderHistoryOrderNo.value.trim().toLowerCase())) return false;
    if (orderHistoryItemName.value.trim() && !order.lines.some((line) => line.name.toLowerCase().includes(orderHistoryItemName.value.trim().toLowerCase()))) return false;
    if (!query) return true;
    return [order.orderNo, order.id, order.note, ...order.lines.map((line) => line.name), ...order.lines.map((line) => line.note)]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });
});
const localOrderColumns = computed<TableColumn<LocalOrderTableRow>[]>(() => [
  { key: "orderNo", label: t("ui.orderNumber") },
  { key: "createdAt", label: t("ui.date") },
  { key: "paymentMethod", label: t("ui.payment") },
  { key: "totalMinor", label: t("ui.total"), align: "right" },
  { key: "actions", label: t("ui.actions"), align: "right" },
]);
const localOrderRows = computed<LocalOrderTableRow[]>(() => visibleOrders.value.map((order) => ({
  id: order.id,
  orderNo: order.orderNo ?? order.id.slice(0, 8),
  createdAt: order.createdAt,
  paymentMethod: order.paymentMethod,
  totalMinor: order.totalMinor,
  status: order.status,
  note: order.note ?? "",
  sourceOrder: order,
})));
const paginatedLocalOrders = computed(() => {
  const start = (localOrderPage.value - 1) * localOrderPageSize.value;
  return localOrderRows.value.slice(start, start + localOrderPageSize.value);
});
watch(visibleOrders, () => { localOrderPage.value = 1; });
watch([localOrderPage, localOrderPageSize, () => visibleOrders.value.length], () => {
  const lastPage = Math.max(1, Math.ceil(visibleOrders.value.length / localOrderPageSize.value));
  if (localOrderPage.value > lastPage) localOrderPage.value = lastPage;
});
function handleLocalOrderPagination(change: PaginationChangeEvent): void {
  localOrderPage.value = change.page;
  localOrderPageSize.value = change.pageSize;
}
const remoteHistoryQuery = computed<AdminOrderQuery>(() => ({
  offset: 0,
  limit: 100,
  sortBy: "createdAt",
  sortDirection: "desc",
  status: orderHistoryStatus.value,
  paymentMethod: orderHistoryPayment.value === "all" ? undefined : orderHistoryPayment.value,
  orderNo: orderHistoryOrderNo.value.trim() || undefined,
  itemName: orderHistoryItemName.value.trim() || undefined,
  minAmountMinor: orderHistoryMinAmount.value === null ? undefined : orderHistoryMinAmount.value,
  maxAmountMinor: orderHistoryMaxAmount.value === null ? undefined : orderHistoryMaxAmount.value,
  from: orderHistoryFrom.value ? posDateBoundaryIso(orderHistoryFrom.value, orderHistoryDatePickerTimeZone.value) : undefined,
  to: orderHistoryTo.value ? posDateBoundaryIso(orderHistoryTo.value, orderHistoryDatePickerTimeZone.value, true) : undefined,
}));

function getBusinessDayConfig(): BusinessDayConfig {
  const startMinutes = Number(readLocalStorage("posnext.business-day-start-minutes") ?? DEFAULT_BUSINESS_DAY_CONFIG.startMinutes);
  return { timeZone: readLocalStorage("posnext.timezone") ?? DEFAULT_BUSINESS_DAY_CONFIG.timeZone, startMinutes: Number.isInteger(startMinutes) ? startMinutes : DEFAULT_BUSINESS_DAY_CONFIG.startMinutes };
}

function formatMoney(minor: number): string {
  return new Intl.NumberFormat(locale.value, { style: "currency", currency: getCurrency() }).format(minor / 100);
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(locale.value, { dateStyle: "medium", timeStyle: "short", timeZone: getBusinessDayConfig().timeZone }).format(new Date(value));
}

function createId(prefix: string): string {
  const randomUuid = globalThis.crypto?.randomUUID?.();
  return randomUuid ? `${prefix}_${randomUuid}` : `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function historyFromForRange(range: InitialOrderHistoryRange): string | undefined {
  if (range === "all") return undefined;
  const from = new Date();
  from.setUTCDate(from.getUTCDate() - Number(range));
  return from.toISOString();
}

function currentSyncHistoryFrom(): string | undefined {
  const saved = readLocalStorage("posnext.sync-history-from");
  if (saved === "all") return undefined;
  if (saved) return saved;
  const from = historyFromForRange("7");
  writeLocalStorage("posnext.sync-history-from", from);
  return from;
}

function lineTotalMinor(line: CartLine): number {
  const optionMinor = (line.options ?? []).reduce((total, option) => total + option.priceMinor, 0);
  return (line.unitPriceMinor + optionMinor) * line.quantity;
}

async function refreshLocalState(): Promise<void> {
  const showInitialLoading = !localStateLoaded.value;
  if (showInitialLoading) loading.value = true;
  errorMessage.value = "";
  try {
    deviceConfigSnapshot.value = await repository.getDeviceConfigSnapshot();
    const snapshotMatchesDevice = deviceConfigSnapshot.value && deviceContext.value
      && deviceConfigSnapshot.value.device.deviceId === deviceContext.value.deviceId
      && deviceConfigSnapshot.value.device.storeId === deviceContext.value.storeId;
    menu.value = snapshotMatchesDevice ? deviceConfigSnapshot.value?.menu?.items ?? [] : [];
    orders.value = (await repository.listOrders()).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    pendingCount.value = (await repository.listPendingMutations()).length;
    conflicts.value = await repository.listConflicts();
    conflictCount.value = conflicts.value.filter((conflict) => !conflict.resolvedAt).length;
    printPendingCount.value = (await repository.listPrintJobs()).filter((job) => job.status === "pending" || job.status === "failed").length;
    if (!categories.value.includes(activeCategory.value)) activeCategory.value = categories.value[0] ?? "Main dish";
  } catch (error) {
    errorMessage.value = error instanceof Error ? `Unable to load local POS data: ${error.message}` : "Unable to load local POS data";
  } finally {
    localStateLoaded.value = true;
    if (showInitialLoading) loading.value = false;
  }
}

async function loadRemoteOperatorHistory(reset = true): Promise<void> {
  if (!operatorSession.value) {
    remoteHistoryHasMore.value = false;
    return;
  }
  remoteHistoryLoading.value = true;
  try {
    const requestedOffset = reset ? 0 : remoteHistoryOffset.value;
    remoteHistoryOffset.value = requestedOffset;
    const remoteOrders = await fetchRemoteOperatorOrders({ ...remoteHistoryQuery.value, offset: requestedOffset }, fetch);
    if (!remoteOrders) {
      remoteHistoryHasMore.value = false;
      return;
    }
    if (remoteOrders.orders.length) await repository.upsertRemoteOrders(remoteOrders.orders);
    remoteHistoryLoaded.value = true;
    remoteHistoryHasMore.value = remoteOrders.hasMore;
    remoteHistoryOffset.value = remoteOrders.nextOffset ?? requestedOffset + remoteOrders.orders.length;
  } catch {
    remoteHistoryHasMore.value = false;
    remoteHistoryLoaded.value = false;
    message.value = "Server order history unavailable; using local history.";
  } finally {
    remoteHistoryLoading.value = false;
  }
}

async function loadMoreRemoteHistory(): Promise<void> {
  if (!remoteHistoryHasMore.value || remoteHistoryLoading.value) return;
  await loadRemoteOperatorHistory(false);
  await refreshLocalStateWithoutRemote();
}

async function refreshRemoteHistoryFilter(): Promise<void> {
  if (!operatorSession.value) return;
  await loadRemoteOperatorHistory(true);
  await refreshLocalStateWithoutRemote();
}

function setOrderHistoryDateRange(value: unknown): void {
  if (!Array.isArray(value) || typeof value[0] !== "string" || typeof value[1] !== "string") {
    orderHistoryFrom.value = "";
    orderHistoryTo.value = "";
  } else if (value[0] <= value[1]) {
    orderHistoryFrom.value = value[0];
    orderHistoryTo.value = value[1];
  } else {
    orderHistoryFrom.value = value[1];
    orderHistoryTo.value = value[0];
  }
  void refreshRemoteHistoryFilter();
}

async function openOrder(order: Order): Promise<void> {
  selectedOrder.value = order;
  selectedOrderEvents.value = (await repository.listEvents()).filter((event) => event.orderId === order.id).sort((left, right) => left.occurredAt.localeCompare(right.occurredAt));
  if (!operatorSession.value) return;
  selectedOrderLoading.value = true;
  try {
    const remote = await fetchRemoteOperatorOrderDetail(order.id, fetch);
    if (remote) {
      selectedOrder.value = remote.order;
      selectedOrderEvents.value = remote.events;
    }
  } catch (error) {
    message.value = error instanceof Error ? error.message : "Remote order detail is unavailable; showing local detail.";
  } finally {
    selectedOrderLoading.value = false;
  }
}

async function refreshLocalStateWithoutRemote(): Promise<void> {
  orders.value = (await repository.listOrders()).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  pendingCount.value = (await repository.listPendingMutations()).length;
  conflicts.value = await repository.listConflicts();
  conflictCount.value = conflicts.value.filter((conflict) => !conflict.resolvedAt).length;
  printPendingCount.value = (await repository.listPrintJobs()).filter((job) => job.status === "pending" || job.status === "failed").length;
  if (!categories.value.includes(activeCategory.value)) activeCategory.value = categories.value[0] ?? "Main dish";
}

function refreshConnection(): void {
  online.value = typeof navigator === "undefined" ? true : navigator.onLine;
  message.value = online.value
    ? operatorUnlocked.value ? "Online detected; local checkout is ready and sync may reconcile pending data." : "Online detected; enter the Operator PIN to unlock checkout."
    : operatorUnlocked.value ? "Offline mode: local checkout remains available on this device." : "Offline mode: enter the configured Operator PIN to unlock checkout.";
}

async function applyPwaUpdate(): Promise<void> {
  pwaUpdateBusy.value = true;
  try {
    const result = await requestPwaUpdate();
    if (result !== "updated") pwaUpdateVisible.value = false;
  } finally {
    pwaUpdateBusy.value = false;
  }
}

function showPwaUpdate(): void {
  pwaUpdateVisible.value = true;
}

async function syncNow(showMessage = false): Promise<void> {
  if (!operatorSession.value) {
    syncState.value = online.value ? "Local only · sign in to sync" : "Offline";
    return;
  }
  if (!online.value) {
    syncState.value = "Offline";
    return;
  }
  try {
    await (syncCoordinator?.trigger() ?? executeSync());
  } catch (error) {
    if (showMessage) message.value = error instanceof Error ? error.message : "Server sync is unavailable";
  }
}

async function executeSync() {
  const result = await new DualStreamSyncClient(
    new SyncClient(repository, getSyncIdentity(), undefined, undefined, undefined, { pullFrom: currentSyncHistoryFrom() }),
    new DeviceConfigSyncClient(repository),
  ).sync();
  lastSyncAt.value = new Date().toISOString();
  writeLocalStorage("posnext.last-sync-at", lastSyncAt.value);
  await refreshLocalState();
  return result;
}

function updateSyncState(snapshot: SyncCoordinatorSnapshot): void {
  if (snapshot.status === "syncing") syncState.value = "Syncing…";
  else if (snapshot.status === "synced" && snapshot.lastResult) syncState.value = snapshot.lastResult.configError
    ? `Orders synced · config refresh pending`
    : `Synced · ${snapshot.lastResult.pushed} pushed · ${snapshot.lastResult.pulled} pulled${snapshot.lastResult.hasMore ? " · more history pending" : ""}`;
  else if (snapshot.status === "failed") syncState.value = `Sync failed; local data retained${snapshot.failureCount > 1 ? ` · ${snapshot.failureCount} failures` : ""}`;
  else if (snapshot.status === "idle" && !online.value) syncState.value = "Offline";
}

function connectionAction(): void {
  refreshConnection();
  void syncNow(true);
}

function can(permission: Permission): boolean {
  return operatorUnlocked.value && hasPermission(currentRole.value, permission);
}

function deviceIsInitialized(): boolean {
  return Boolean(deviceContext.value && deviceConfigSnapshot.value
    && deviceConfigSnapshot.value.device.deviceId === deviceContext.value.deviceId
    && deviceConfigSnapshot.value.device.storeId === deviceContext.value.storeId);
}

async function authorizeOwnerBootstrap(): Promise<void> {
  authBusy.value = true;
  authError.value = "";
  try {
    const result = await authClient.authorizeBootstrap({
      method: "owner-password",
      email: ownerBootstrap.value.email.trim(),
      secret: ownerBootstrap.value.secret,
    });
    ownerBootstrapStores.value = result.stores;
    ownerBootstrapCanCreateStore.value = result.canCreateStore;
    if (result.stores.length === 1) ownerBootstrapSelection.value.storeId = result.stores[0]!.storeId;
    if (!result.stores.length) ownerBootstrapSelection.value.mode = "new";
    ownerBootstrapStep.value = "store";
    authError.value = "";
  } catch (error) {
    authError.value = error instanceof Error ? error.message : "Owner authentication failed";
  } finally {
    authBusy.value = false;
  }
}

function resetOwnerBootstrap(): void {
  ownerBootstrapStep.value = "credentials";
  ownerBootstrap.value.secret = "";
  ownerBootstrapStores.value = [];
  ownerBootstrapCanCreateStore.value = false;
  authError.value = "";
}

function selectBootstrapRegion(option: { code: string; name: string }): void {
  ownerBootstrapSelection.value.region = option.name.split(" · ")[0] ?? option.name;
  ownerBootstrapRegionSearch.value = option.name;
}

function selectBootstrapTimezone(option: { value: string; name: string }): void {
  ownerBootstrapSelection.value.timeZone = option.value;
  ownerBootstrapTimezoneSearch.value = option.name;
}

async function bootstrapOwnerDevice(): Promise<void> {
  authBusy.value = true;
  authError.value = "";
  try {
    const clientId = ensureSharedOperatorClientId(() => createId("client"));
    const selection: DeviceBootstrapStoreSelection = ownerBootstrapSelection.value.mode === "existing"
      ? { mode: "existing", storeId: ownerBootstrapSelection.value.storeId }
      : {
          mode: "new",
          storeId: ownerBootstrapSelection.value.storeId.trim(),
          name: ownerBootstrapSelection.value.name.trim(),
          region: ownerBootstrapSelection.value.region.trim(),
          timeZone: ownerBootstrapSelection.value.timeZone.trim(),
          currency: ownerBootstrapSelection.value.currency,
          businessDayStartMinutes: posTimeToMinutes(ownerBootstrapBusinessDayTime.value),
          deviceLabel: ownerBootstrapSelection.value.deviceLabel.trim(),
        };
    const result = await authClient.bootstrapDevice({
      method: "owner-password",
      email: ownerBootstrap.value.email.trim(),
      secret: ownerBootstrap.value.secret,
      clientId,
      storeSelection: selection,
    });
    await repository.applyDeviceConfigSnapshot(result.snapshot);
    storeOperatorDeviceContext(result.context);
    deviceContext.value = result.context;
    deviceConfigSnapshot.value = result.snapshot;
    ownerBootstrap.value.secret = "";
    ownerBootstrapStep.value = "credentials";
    writeLocalStorage("posnext.owner-email", ownerBootstrap.value.email.trim().toLowerCase());
    writeLocalStorage("posnext.store-id", result.context.storeId);
    writeLocalStorage("posnext.device-id", result.context.deviceId);
    writeLocalStorage("posnext.sync-history-from", historyFromForRange(ownerBootstrapHistoryRange.value) ?? "all");
    authError.value = "";
    message.value = "Device initialized. Enter the numeric Operator PIN to unlock POS.";
    await refreshLocalState();
  } catch (error) {
    authError.value = error instanceof Error ? error.message : "Device initialization failed";
  } finally {
    authBusy.value = false;
  }
}

async function refreshDeviceConfig(): Promise<void> {
  if (!operatorSession.value || !deviceIsInitialized()) return;
  const current = deviceConfigSnapshot.value?.configRevision ?? 0;
  const result = await authClient.deviceConfig(current);
  if (!result.changed) return;
  await repository.applyDeviceConfigSnapshot(result.snapshot);
  deviceConfigSnapshot.value = result.snapshot;
  menu.value = result.snapshot.menu?.items ?? [];
  if (!categories.value.includes(activeCategory.value)) activeCategory.value = categories.value[0] ?? "Main dish";
  message.value = `Device configuration updated to revision ${result.snapshot.configRevision}.`;
}

async function checkOperatorSession(): Promise<void> {
  try {
    operatorSession.value = await authClient.session();
    if (!operatorSession.value) return;
    if (!deviceIsInitialized() || deviceContext.value?.storeId !== operatorSession.value.storeId || deviceContext.value?.deviceId !== operatorSession.value.deviceId) {
      await authClient.logout().catch(() => undefined);
      operatorSession.value = null;
      return;
    }
    operatorUnlocked.value = true;
    currentRole.value = operatorSession.value.role;
    writeLocalStorage("posnext.operator-display-name", operatorSession.value.displayName);
    writeLocalStorage("posnext.actor-id", operatorSession.value.actorId);
    writeLocalStorage("posnext.store-id", operatorSession.value.storeId);
    writeLocalStorage("posnext.device-id", operatorSession.value.deviceId);
  } catch {
    operatorSession.value = null;
  }
}

async function revalidateOperatorSession(): Promise<void> {
  if (!operatorSession.value) return;
  try {
    const next = await authClient.session();
    if (!next) {
      operatorSession.value = null;
      operatorUnlocked.value = false;
      currentRole.value = "operator";
      syncCoordinator?.stop();
      syncState.value = "Session expired; local PIN unlock available.";
      message.value = "Operator session expired or was revoked. Enter the PIN to continue locally or reconnect to sync.";
      return;
    }
    operatorSession.value = next;
    currentRole.value = next.role;
  } catch {
    // A temporary network failure must not turn a local-first session into a false logout.
  }
}

async function loginOperator(): Promise<void> {
  if (!deviceIsInitialized() || !deviceContext.value) {
    authError.value = "Ask the owner or an Admin to initialize this POS before entering an Operator PIN.";
    return;
  }
  authBusy.value = true;
  authError.value = "";
  try {
    const pin = operatorPin.value.trim();
    if (!/^\d{4,12}$/.test(pin)) throw new Error("PIN must contain 4 to 12 digits");
    if (online.value) {
      const session = await authClient.login({ method: "operator-pin", pin, storeId: deviceContext.value.storeId, deviceId: deviceContext.value.deviceId });
      operatorSession.value = session;
      currentRole.value = session.role;
      operatorUnlocked.value = true;
      localOperatorUnlock.value = await createLocalOperatorUnlock({ displayName: session.displayName, actorId: session.actorId, storeId: session.storeId, deviceId: session.deviceId, pin });
      writeLocalStorage("posnext.operator-unlock", JSON.stringify(localOperatorUnlock.value));
      writeLocalStorage("posnext.operator-display-name", session.displayName);
      writeLocalStorage("posnext.actor-id", session.actorId);
      writeLocalStorage("posnext.store-id", session.storeId);
      writeLocalStorage("posnext.device-id", session.deviceId);
      await refreshDeviceConfig();
      scheduleSync();
      message.value = "Operator unlocked; server sync is active when online.";
      await syncNow(true);
    } else {
      const operators = await repository.listDeviceOperators();
      const snapshotOperator = await findDeviceOperatorByPin(pin, operators);
      const record = localOperatorUnlock.value;
      if (snapshotOperator) {
        localOperatorUnlock.value = await createLocalOperatorUnlock({ displayName: snapshotOperator.displayName, actorId: snapshotOperator.actorId, storeId: deviceContext.value.storeId, deviceId: deviceContext.value.deviceId, pin });
        writeLocalStorage("posnext.operator-unlock", JSON.stringify(localOperatorUnlock.value));
        currentRole.value = snapshotOperator.role;
      } else if (!record || record.storeId !== deviceContext.value.storeId || record.deviceId !== deviceContext.value.deviceId || !(await verifyLocalOperatorPin(pin, record))) {
        throw new Error("Offline PIN is not available for this device");
      }
      operatorSession.value = null;
      operatorUnlocked.value = true;
      syncState.value = "Offline · local checkout";
      message.value = "Operator unlocked locally; orders will remain queued until the server is available.";
    }
    operatorPin.value = "";
  } catch (error) {
    operatorPin.value = "";
    authError.value = error instanceof Error ? error.message : "Operator sign-in failed";
  } finally {
    authBusy.value = false;
  }
}

async function logoutOperator(): Promise<void> {
  authBusy.value = true;
  try {
    if (operatorSession.value) await authClient.logout();
    operatorSession.value = null;
    operatorUnlocked.value = false;
    currentRole.value = "operator";
    remoteHistoryOffset.value = 0;
    remoteHistoryHasMore.value = false;
    remoteHistoryLoaded.value = false;
    scheduleSync();
    syncState.value = "Locked";
    message.value = "Operator signed out; enter the PIN before using POS.";
  } catch (error) {
    authError.value = error instanceof Error ? error.message : "Operator sign-out failed";
  } finally {
    authBusy.value = false;
  }
}

function appendOperatorPin(digit: string): void {
  if (operatorPin.value.length < 12) operatorPin.value += digit;
}

function clearOperatorPin(): void {
  operatorPin.value = "";
}

function backspaceOperatorPin(): void {
  operatorPin.value = operatorPin.value.slice(0, -1);
}

function loadSettings(): void {
  const stored = readLocalStorage("posnext.settings");
  if (!stored) return;
  try {
    settings.value = { ...settings.value, ...(JSON.parse(stored) as Partial<LocalSettings>) };
    settings.value.receiptTransport ??= settings.value.printTransport;
    settings.value.kitchenTransport ??= settings.value.printTransport;
    settings.value.printerProfiles ??= [];
    settings.value.syncIntervalSeconds = Math.max(15, Number(settings.value.syncIntervalSeconds) || 60);
    applyAppearance();
  } catch {
    message.value = "Saved settings were invalid; using local defaults.";
  }
}

function applyAppearance(): void {
  const dark = settings.value.theme === "dark" || (settings.value.theme === "system" && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
  document.documentElement.lang = languageTag(settings.value.language);
}

// Apply the persisted theme before the first render so teleported shared UI
// primitives (including BaseDialog) receive the same theme as the app shell.
applyAppearance();

function scheduleSync(): void {
  if (!syncCoordinator || !operatorSession.value) {
    syncCoordinator?.stop();
    return;
  }
  if (settings.value.autoSync) syncCoordinator.start(settings.value.syncIntervalSeconds);
  else syncCoordinator.stop();
}

function saveSettings(): void {
  settings.value.syncIntervalSeconds = Math.max(15, Number(settings.value.syncIntervalSeconds) || 60);
  writeLocalStorage("posnext.settings", JSON.stringify(settings.value));
  applyAppearance();
  scheduleSync();
  message.value = "Local settings saved. Remote settings are not changed by this program.";
}

async function updateApp(): Promise<void> {
  try {
    const result = await requestPwaUpdate();
    message.value = result === "updated" ? "App update requested; reloading the latest static assets." : result === "checked" ? "App is already up to date." : "PWA updates are unavailable in this browser.";
    if (result === "updated") window.setTimeout(() => window.location.reload(), 250);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to check for app updates";
  }
}

async function toggleAppFullscreen(): Promise<void> {
  try {
    const result = await toggleFullscreen();
    fullscreen.value = result === "entered";
    if (result === "unavailable") message.value = "Fullscreen is unavailable in this browser.";
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to change fullscreen mode";
  }
}

async function resetLocalData(): Promise<void> {
  if (!can("settings.manage")) return;
  if (!window.confirm("Clear this browser's local POS data, including unsynced orders and print jobs? Server data will not be deleted.")) return;
  syncCoordinator?.stop();
  await repository.clearLocalData();
  window.location.reload();
}

async function checkDevices(): Promise<void> {
  webSerialAvailable.value = await new WebSerialPrintAdapter().isAvailable();
  const bridge = new LocalPrintBridgeAdapter(settings.value.bridgeEndpoint);
  bridgeAvailable.value = await bridge.isAvailable();
  bridgeLifecycle.value = await bridge.status();
  hardwareStatus.value = evaluateHardware({
    configuredTransport: settings.value.printTransport,
    capabilities: {
      webSerial: webSerialAvailable.value,
      localBridge: bridgeAvailable.value,
      bluetooth: bridgeAvailable.value,
      usb: bridgeAvailable.value,
      cashDrawer: bridgeAvailable.value,
      cutter: bridgeAvailable.value,
    },
  });
  message.value = "Device capability check completed; no device was paired or modified.";
}

function createPrinterProfile(): void {
  if (!can("settings.manage")) return;
  const id = window.prompt("Printer profile ID:", `printer-${settings.value.printerProfiles.length + 1}`)?.trim();
  if (!id) return;
  const label = window.prompt("Printer label:", "Counter printer")?.trim();
  if (!label) return;
  const transport = window.prompt("Transport: web-serial, local-bridge, bluetooth or usb:", settings.value.printTransport === "none" ? "local-bridge" : settings.value.printTransport)?.trim() as NamedPrinterDeviceProfile["transport"];
  const paperWidth = Number(window.prompt("Paper width: 58 or 80:", String(settings.value.paperWidth))) as NamedPrinterDeviceProfile["paperWidth"];
  const deviceAddress = transport === "bluetooth" || transport === "usb" ? window.prompt("Bridge device address:")?.trim() : undefined;
  const profile: NamedPrinterDeviceProfile = { id, label, transport, paperWidth, bridgeEndpoint: settings.value.bridgeEndpoint, ...(deviceAddress ? { deviceAddress } : {}), enabled: true };
  try {
    validateNamedPrinterDeviceProfile(profile);
    settings.value.printerProfiles = [...settings.value.printerProfiles, profile];
    saveSettings();
    message.value = `Printer profile ${id} saved locally.`;
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Unable to create printer profile";
  }
}

async function installBridge(): Promise<void> {
  bridgeLifecycle.value = await new LocalPrintBridgeAdapter(settings.value.bridgeEndpoint).install();
  message.value = bridgeLifecycle.value.state === "connected" ? "Local bridge installed and connected." : `Local bridge status: ${bridgeLifecycle.value.state}.`;
}

async function pairPrinterProfile(profile: NamedPrinterDeviceProfile): Promise<void> {
  bridgeLifecycle.value = await new LocalPrintBridgeAdapter(profile.bridgeEndpoint ?? settings.value.bridgeEndpoint).pair(profile);
  message.value = `Printer ${profile.label} pairing state: ${bridgeLifecycle.value.state}.`;
}

async function reconnectPrinterProfile(profile: NamedPrinterDeviceProfile): Promise<void> {
  bridgeLifecycle.value = await new LocalPrintBridgeAdapter(profile.bridgeEndpoint ?? settings.value.bridgeEndpoint).reconnect(profile);
  message.value = `Printer ${profile.label} reconnect state: ${bridgeLifecycle.value.state}.`;
}

async function processPrintQueue(): Promise<void> {
  const routes: PrintRouteConfig = { receipt: settings.value.receiptTransport, kitchen: settings.value.kitchenTransport };
  if (routes.receipt === "none" && routes.kitchen === "none") {
    message.value = "Choose a Web Serial or local bridge route before processing print jobs.";
    return;
  }
  const serial = () => new WebSerialPrintAdapter();
  const bridge = () => new LocalPrintBridgeAdapter(settings.value.bridgeEndpoint);
  const adapterForRoute = (route: PrintRouteConfig["receipt"]) => route === "web-serial" ? serial() : route === "local-bridge" || route === "bluetooth" || route === "usb" ? bridge() : undefined;
  const adapters = {
    receipt: adapterForRoute(routes.receipt),
    kitchen: adapterForRoute(routes.kitchen),
  };
  const result = await processPendingPrintJobs(repository, adapters);
  message.value = result.attempted === 0
    ? "No pending print jobs."
    : `Print queue processed: ${result.printed} printed, ${result.failed} failed; orders remain locally retained.`;
  await refreshLocalState();
}

function getCurrency(): "GBP" | "USD" | "EUR" | "MOP" {
  const value = readLocalStorage("posnext.currency");
  return value === "USD" || value === "EUR" || value === "MOP" ? value : "GBP";
}

function selectView(next: View): void {
  if (!operatorUnlocked.value) {
    authError.value = "Enter the Operator PIN before using POS.";
    return;
  }
  view.value = next;
  if (next === "orders" && operatorSession.value && !remoteHistoryLoaded.value) void loadRemoteOperatorHistory(true).then(() => refreshLocalStateWithoutRemote());
}

function addToCart(item: MenuItem): void {
  if (!operatorUnlocked.value) {
    authError.value = "Enter the Operator PIN before adding items.";
    return;
  }
  if (item.optionGroups?.length) {
    customizingItem.value = item;
    customOptions.value = {};
    itemNote.value = "";
    return;
  }
  addConfiguredLine(item, [], "");
}

function addConfiguredLine(item: MenuItem, options: SelectedOption[], note: string): void {
  if (!operatorUnlocked.value) return;
  const signature = `${item.id}:${options.map((option) => option.optionId).sort().join(",")}:${note}`;
  const existing = cart.value.find((line) => `${line.productId}:${(line.options ?? []).map((option) => option.optionId).sort().join(",")}:${line.note ?? ""}` === signature);
  if (existing) existing.quantity += 1;
  else cart.value.push({ productId: item.id, name: item.name, unitPriceMinor: item.priceMinor, quantity: 1, options, note: note || undefined });
  message.value = `${item.name} added to cart`;
  errorMessage.value = "";
}

function confirmCustomization(): void {
  const item = customizingItem.value;
  if (!item) return;
  let options: SelectedOption[];
  try {
    options = selectedOptionsForMenuItem(item, customOptions.value);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Selected menu options are invalid";
    return;
  }
  addConfiguredLine(item, options, itemNote.value.trim());
  customizingItem.value = null;
}

function changeQuantity(productId: string, delta: number): void {
  const line = cart.value.find((item) => item.productId === productId);
  if (!line) return;
  line.quantity += delta;
  if (line.quantity <= 0) cart.value = cart.value.filter((item) => item.productId !== productId);
}

function setPaymentMethod(method: PaymentMethod): void {
  paymentMethod.value = method;
  cardConfirmed.value = false;
  cardReference.value = "";
  if (method === "cash") mixedCashMinor.value = 0;
  if (method === "card") cashReceivedMinor.value = null;
}

function resetPaymentInputs(): void {
  cashReceivedMinor.value = null;
  mixedCashMinor.value = Math.floor(cartTotalMinor.value / 2);
  cardConfirmed.value = false;
  cardReference.value = "";
}

async function checkout(): Promise<void> {
  if (!operatorUnlocked.value) {
    authError.value = "Enter the Operator PIN before checkout.";
    return;
  }
  if (cart.value.length === 0 || busy.value) return;
  busy.value = true;
  message.value = "";
  errorMessage.value = "";
  try {
    if (discountMinor.value < 0 || discountMinor.value + discountTypeMinor.value > cartSubtotalMinor.value) throw new Error("Discount must be between zero and subtotal");
    if (discountMinor.value + discountTypeMinor.value > 0 && !discountReason.value.trim()) throw new Error("A discount reason is required");
    if (paymentMethod.value === "cash" && (cashReceivedMinor.value ?? 0) < cartTotalMinor.value) throw new Error("Cash received is less than the amount due");
    if (paymentMethod.value === "mixed" && (mixedCashMinor.value <= 0 || mixedCashMinor.value >= cartTotalMinor.value)) throw new Error("Mixed payment requires both cash and card amounts");
    const cardCapture = cardDueMinor.value > 0
      ? manualCardPayment.confirm({ amountMinor: cardDueMinor.value, currency: getCurrency(), operatorConfirmed: cardConfirmed.value, reference: cardReference.value })
      : undefined;
    const occurredAt = new Date().toISOString();
    const businessDate = businessDateFor(occurredAt, getBusinessDayConfig());
    const orderNo = await repository.nextOrderNumber(getStoreId(), businessDate);
    const result = createCompletedOrder({
      orderId: createId("order"),
      mutationId: createId("mutation"),
      clientId: getClientId(),
      storeId: getStoreId(),
      actorId: getActorId(),
      deviceId: getDeviceId(),
      occurredAt,
      currency: getCurrency(),
      lines: cart.value.map((line) => ({ ...line })),
      discountMinor: discountMinor.value,
      discountType: discountType.value,
      discountReason: discountReason.value.trim() || undefined,
      note: orderNote.value.trim() || undefined,
      paymentMethod: paymentMethod.value,
      payment: {
        cashMinor: cashDueMinor.value,
        cardMinor: cardDueMinor.value,
        cashReceivedMinor: paymentMethod.value === "cash" ? cashReceivedMinor.value ?? undefined : undefined,
        cardMode: cardCapture?.mode,
        cardReference: cardCapture?.reference,
      },
      orderNo,
      businessDate,
    });
    const printJobs: LocalPrintJob[] = createPrintJobs({
      orderId: result.order.id,
      createdAt: occurredAt,
      paperWidth: settings.value.paperWidth,
      mode: settings.value.printCopies,
      idFactory: (kind) => createId(`print-${kind}`),
      language: settings.value.language,
      routes: { receipt: settings.value.receiptTransport, kitchen: settings.value.kitchenTransport },
    });
    await repository.persistCompletedOrder(result.order, result.mutation, printJobs);
    cart.value = [];
    discountMinor.value = 0;
    discountType.value = "none";
    discountReason.value = "";
    orderNote.value = "";
    resetPaymentInputs();
    message.value = `Order saved locally: ${formatMoney(result.order.totalMinor)}. It is queued for server sync.`;
    await refreshLocalState();
    view.value = "orders";
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Checkout could not be saved locally";
  } finally {
    busy.value = false;
  }
}

async function recordNoSale(): Promise<void> {
  if (!can("cash-drawer.open")) {
    errorMessage.value = "This role cannot open the cash drawer";
    return;
  }
  const event: OrderEvent = { id: createId("event"), type: "no-sale", storeId: getStoreId(), actorId: getActorId(), deviceId: getDeviceId(), occurredAt: new Date().toISOString(), amountMinor: 0 };
  await repository.persistEvent(event, createEventMutation(event, createId("mutation")));
  message.value = "No Sale recorded locally; drawer action remains hardware-gated.";
  await refreshLocalState();
}

async function correctOrder(order: Order, type: "void" | "refund"): Promise<void> {
  const permission = type === "void" ? "orders.void" : "orders.refund";
  if (!can(permission)) {
    errorMessage.value = `Your role cannot ${type} orders`;
    return;
  }
  const reason = window.prompt(`${type === "void" ? "Void" : "Refund"} reason`, "Customer request")?.trim();
  if (!reason) return;
  const refundMethod = type === "refund" ? window.prompt("Refund method (cash/card)", "cash")?.trim().toLowerCase() : undefined;
  if (type === "refund" && refundMethod !== "cash" && refundMethod !== "card") {
    errorMessage.value = "Refund method must be cash or card";
    return;
  }
  const event: OrderEvent = { id: createId("event"), orderId: order.id, type, storeId: getStoreId(), actorId: getActorId(), deviceId: getDeviceId(), occurredAt: new Date().toISOString(), amountMinor: order.totalMinor, paymentMethod: order.paymentMethod, refundMethod: refundMethod as RefundMethod | undefined, reason };
  await repository.persistEvent(event, createEventMutation(event, createId("mutation")));
  if (type === "void") await repository.updateLocalOrder({ ...order, status: "voided", updatedAt: event.occurredAt, version: order.version + 1 });
  message.value = `${type === "void" ? "Void" : "Refund"} recorded locally for ${order.orderNo ?? order.id}.`;
  await refreshLocalState();
}

async function reprintOrder(order: Order): Promise<void> {
  if (!can("orders.reprint")) {
    errorMessage.value = "Your role cannot reprint orders";
    return;
  }
  const reason = window.prompt("Reprint reason", "Customer copy")?.trim();
  if (!reason) return;
  const occurredAt = new Date().toISOString();
  const event: OrderEvent = { id: createId("event"), orderId: order.id, type: "reprint", storeId: getStoreId(), actorId: getActorId(), deviceId: getDeviceId(), occurredAt, amountMinor: order.totalMinor, paymentMethod: order.paymentMethod, reason };
  const original = (await repository.listPrintJobs()).find((job) => job.orderId === order.id && job.kind === "receipt") ?? { id: `receipt-original-${order.id}`, orderId: order.id, kind: "receipt" as const, status: "printed" as const, paperWidth: settings.value.paperWidth, createdAt: order.createdAt, attemptCount: 1 };
  await repository.persistEvent(event, createEventMutation(event, createId("mutation")), [createReprintJob({ ...original, language: settings.value.language }, createId("print-reprint"), occurredAt)]);
  message.value = `Reprint queued locally for ${order.orderNo ?? order.id}.`;
  await refreshLocalState();
}

function getClientId(): string {
  if (deviceContext.value?.clientId) {
    writeLocalStorage("posnext.client-id", deviceContext.value.clientId);
    return deviceContext.value.clientId;
  }
  const key = "posnext.client-id";
  const existing = readLocalStorage(key);
  if (existing) return existing;
  const clientId = createId("client");
  writeLocalStorage(key, clientId);
  return clientId;
}

function getStoreId(): string {
  if (!deviceContext.value) throw new Error("Operator device is not configured");
  return deviceContext.value.storeId;
}

function getActorId(): string {
  const actorId = operatorSession.value?.actorId ?? localOperatorUnlock.value?.actorId;
  if (!actorId || !operatorUnlocked.value) throw new Error("Operator is locked");
  return actorId;
}

function getDeviceId(): string {
  const deviceId = operatorSession.value?.deviceId ?? deviceContext.value?.deviceId;
  if (!deviceId || !operatorUnlocked.value) throw new Error("Operator device is locked");
  return deviceId;
}

function getSyncIdentity(): SyncIdentity {
  return {
    clientId: getClientId(),
    storeId: getStoreId(),
    actorId: getActorId(),
    deviceId: getDeviceId(),
  };
}

onMounted(async () => {
  loadSettings();
  await applyUiLanguage();
  syncCoordinator = new SyncCoordinator(executeSync, undefined, updateSyncState);
  deviceConfigSnapshot.value = await repository.getDeviceConfigSnapshot();
  if (!deviceIsInitialized()) deviceContext.value = null;
  // An uninitialized browser has no tenant/device session to restore. Avoid a
  // deliberately unauthenticated probe so first-run setup is quiet in DevTools.
  if (deviceIsInitialized()) await checkOperatorSession();
  shellLoading.value = false;
  scheduleSync();
  sessionCheckTimer = window.setInterval(() => { void revalidateOperatorSession(); }, 60_000);
  void refreshLocalState();
  window.setTimeout(() => void syncNow(), 0);
  window.addEventListener("online", refreshConnection);
  window.addEventListener("offline", refreshConnection);
  window.addEventListener(PWA_UPDATE_EVENT, showPwaUpdate);
  document.addEventListener("fullscreenchange", () => { fullscreen.value = Boolean(document.fullscreenElement); });
});

watch(() => settings.value.language, () => { void applyUiLanguage(); });
onUpdated(() => { translateDocument(settings.value.language); });

onBeforeUnmount(() => {
  syncCoordinator?.stop();
  if (sessionCheckTimer !== undefined) window.clearInterval(sessionCheckTimer);
  window.removeEventListener("online", refreshConnection);
  window.removeEventListener("offline", refreshConnection);
  window.removeEventListener(PWA_UPDATE_EVENT, showPwaUpdate);
});
</script>

<template>
  <div class="min-h-screen bg-[var(--pn-bg)] text-[var(--pn-text)]">
    <AppLoadingOverlay v-if="shellLoading" app-name="POSNext" icon-src="/icon.svg?v=20260814" />
    <header class="sticky top-0 z-10 border-b border-[var(--pn-border)] bg-[var(--pn-surface)]/95 backdrop-blur">
      <div class="mx-auto flex max-w-[1600px] items-center gap-3 px-4 py-3 lg:px-6">
        <BaseButton class="p-2" variant="ghost" aria-label="Open navigation">☰</BaseButton>
        <div class="mr-auto flex items-center gap-2">
          <img src="/icon.svg?v=20260814" alt="" class="size-9 rounded-xl" />
          <div>
            <div class="font-semibold tracking-tight">POSNext</div>
            <div class="text-[var(--pn-text-muted)] text-xs">Local-first POS</div>
          </div>
        </div>
        <div class="text-[var(--pn-text-muted)] hidden items-center gap-2 text-xs sm:flex">
          <span class="size-2 rounded-full" :class="online ? 'bg-[var(--pn-success)]' : 'bg-[var(--pn-warning)]'" />
          {{ online ? 'Online' : 'Offline' }} · {{ syncState }} · {{ pendingCount }} sync · {{ conflictCount }} conflict · {{ printPendingCount }} print<span v-if="lastSyncAt"> · last {{ formatDate(lastSyncAt) }}</span>
        </div>
        <BaseButton class="!hidden sm:!inline-flex" size="sm" @click="connectionAction">
          Connection
        </BaseButton>
        <BaseButton class="!hidden sm:!inline-flex" size="sm" @click="toggleAppFullscreen">{{ fullscreen ? 'Exit fullscreen' : 'Fullscreen' }}</BaseButton>
        <BasePopoverButton v-model:visible="operatorPopoverOpen" class="w-10 min-w-0 shrink-0 justify-center overflow-hidden px-0 sm:w-auto sm:px-[.55rem]" variant="secondary" size="sm" placement="bottom-end" has-popup="menu" aria-label="Operator device menu">
            <span class="hidden sm:inline">{{ deviceIsInitialized() ? (operatorUnlocked ? operatorDisplayName : 'Locked') : 'Setup' }}</span>
          <template #popover>
            <div class="px-3 py-2 text-xs">
              <div class="font-semibold">{{ deviceIsInitialized() ? deviceContext?.label : 'Device setup required' }}</div>
              <div class="text-[var(--pn-text-muted)] mt-1">{{ operatorUnlocked ? currentRole : 'Locked' }}</div>
            </div>
            <BaseButton v-if="operatorUnlocked" class="mt-1 w-full justify-start" variant="ghost" size="sm" :disabled="authBusy" @click="operatorPopoverOpen = false; void logoutOperator()">Lock</BaseButton>
          </template>
        </BasePopoverButton>
      </div>
    </header>

    <main v-if="!deviceIsInitialized()" class="mx-auto max-w-2xl px-4 py-10 lg:px-6">
      <section class="bg-[var(--pn-surface)] text-[var(--pn-text)] rounded-2xl border border-[var(--pn-border)] p-6 shadow-xl sm:p-8">
        <div class="flex items-center gap-3">
          <span class="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--pn-selection-bg)] text-lg" aria-hidden="true">🔒</span>
          <div>
            <p class="text-sm font-medium text-[var(--pn-selection-text)]">Device setup</p>
            <h1 class="mt-1 text-3xl font-semibold tracking-tight">Initialize device</h1>
          </div>
        </div>
        <p class="text-[var(--pn-text-muted)] mt-4 text-sm leading-6">Sign in once to load this device.</p>
        <form v-if="ownerBootstrapStep === 'credentials'" class="mt-6 space-y-4" @submit.prevent="authorizeOwnerBootstrap">
          <label class="text-[var(--pn-text-muted)] block text-sm"><span>Email</span><BaseInput v-model="ownerBootstrap.email" class="mt-1" autocomplete="email" required type="email" /></label>
          <label class="text-[var(--pn-text-muted)] block text-sm"><span>Password</span><BaseInput v-model="ownerBootstrap.secret" class="mt-1" autocomplete="current-password" required type="password" /></label>
          <BaseButton class="w-full" type="submit" variant="primary" size="lg" :disabled="authBusy">{{ authBusy ? 'Signing in…' : 'Sign in' }}</BaseButton>
        </form>
        <form v-else class="mt-6 space-y-4" @submit.prevent="bootstrapOwnerDevice">
          <BaseAlert severity="success" :dismissible="false">Signed in. Select a Store.</BaseAlert>
          <label class="text-[var(--pn-text-muted)] block text-sm"><span>{{ t("ui.storeSetup") }}</span><PosSelect v-model="ownerBootstrapSelection.mode" :options="ownerBootstrapModeOptions" class="mt-1" /></label>
          <template v-if="ownerBootstrapSelection.mode === 'existing'">
            <label class="text-[var(--pn-text-muted)] block text-sm"><span>{{ t("ui.store") }}</span><PosSelect v-model="ownerBootstrapSelection.storeId" :options="ownerBootstrapStoreOptions" class="mt-1" required /></label>
            <label class="text-[var(--pn-text-muted)] block text-sm"><span>{{ t("ui.initialOrderHistory") }}</span><PosSelect v-model="ownerBootstrapHistoryRange" :options="ownerBootstrapHistoryRangeOptions" class="mt-1" /></label>
            <p class="text-[var(--pn-text-muted)] text-xs">{{ t("ui.initialOrderHistoryHelp") }}</p>
            <p class="text-[var(--pn-text-muted)] text-xs">{{ t("ui.existingStoreDefaultDeviceLabel") }}</p>
          </template>
          <template v-else>
            <div class="grid gap-4 sm:grid-cols-2">
              <label class="text-[var(--pn-text-muted)] block text-sm"><span>{{ t("ui.storeCode") }}</span><BaseInput v-model="ownerBootstrapSelection.storeId" class="mt-1" required placeholder="GB_NEW_STORE" /></label>
              <label class="text-[var(--pn-text-muted)] block text-sm"><span>{{ t("ui.storeName") }}</span><BaseInput v-model="ownerBootstrapSelection.name" class="mt-1" required placeholder="Worthing Branch 2" /></label>
              <label class="text-[var(--pn-text-muted)] block text-sm"><span>{{ t("ui.region") }}</span><BaseAutocomplete v-model="ownerBootstrapRegionSearch" :options="bootstrapRegionOptions" item-key="id" display-key="name" :search-keys="['name', 'code', 'searchText']" :placeholder="t('ui.searchRegion')" class="mt-1 w-full" @select="selectBootstrapRegion" /></label>
              <label class="text-[var(--pn-text-muted)] block text-sm"><span>{{ t("ui.timezone") }}</span><BaseAutocomplete v-model="ownerBootstrapTimezoneSearch" :options="bootstrapTimezoneOptions" item-key="id" display-key="name" :search-keys="['name', 'value', 'searchText']" :placeholder="t('ui.searchTimezone')" class="mt-1 w-full" @select="selectBootstrapTimezone" /></label>
              <label class="text-[var(--pn-text-muted)] block text-sm"><span>{{ t("ui.currency") }}</span><PosSelect v-model="ownerBootstrapSelection.currency" :options="currencyOptions" class="mt-1" /></label>
              <label class="text-[var(--pn-text-muted)] block text-sm"><span>{{ t("ui.businessDayStartTime") }}</span><BaseDatePicker v-model="ownerBootstrapBusinessDayTime" mode="time" :adapter="posTimeAdapter" :time-zone="ownerBootstrapSelection.timeZone" locale="en-GB" display-format="HH:mm" :aria-label="t('ui.businessDayStartTime')" class="mt-1 w-full" /></label>
            </div>
            <label class="text-[var(--pn-text-muted)] block text-sm"><span>{{ t("ui.deviceLabel") }}</span><BaseInput v-model="ownerBootstrapSelection.deviceLabel" class="mt-1" required placeholder="Front counter" /></label>
          </template>
          <div class="flex gap-3"><BaseButton class="flex-1" type="button" :disabled="authBusy" @click="resetOwnerBootstrap">{{ t("ui.back") }}</BaseButton><BaseButton class="flex-1" type="submit" variant="primary" :disabled="authBusy">{{ authBusy ? t("ui.initializing") : t("ui.initializePos") }}</BaseButton></div>
        </form>
        <BaseAlert v-if="authError" class="mt-4" severity="warning" @close="authError = ''">{{ authError }}</BaseAlert>
      </section>
    </main>

    <main v-else-if="!operatorUnlocked" class="mx-auto max-w-lg px-4 py-10 lg:px-6">
      <section class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-6 shadow-sm sm:p-8">
        <p class="text-sm font-medium text-[var(--pn-selection-text)]">{{ deviceContext.label }} · {{ deviceContext.storeId }}</p>
        <h1 class="mt-2 text-3xl font-semibold tracking-tight">{{ t("ui.posStaffSignIn") }}</h1>
        <p class="mt-3 text-sm leading-6 text-[var(--pn-text-subtle)]">Enter your PIN to unlock sales. No menu, order, or checkout action is available until a valid PIN is entered. When the server is unavailable, the registered local PIN can still unlock this device.</p>
        <form class="mt-6" @submit.prevent="loginOperator">
          <div class="rounded-xl border border-[var(--pn-border)] bg-[var(--pn-surface-muted)] px-4 py-4 text-center text-2xl tracking-[0.5em]">{{ operatorPin ? '•'.repeat(operatorPin.length) : 'Enter PIN' }}</div>
          <div class="mt-4 grid grid-cols-3 gap-3">
            <BaseButton v-for="digit in ['1','2','3','4','5','6','7','8','9']" :key="digit" class="py-4 text-xl" @click="appendOperatorPin(digit)">{{ digit }}</BaseButton>
            <BaseButton class="py-4 text-sm" @click="clearOperatorPin">Clear</BaseButton>
            <BaseButton class="py-4 text-xl" @click="appendOperatorPin('0')">0</BaseButton>
            <BaseButton class="py-4 text-sm" aria-label="Backspace" @click="backspaceOperatorPin">⌫</BaseButton>
          </div>
          <BaseButton class="mt-4 w-full" type="submit" variant="primary" size="lg" :disabled="authBusy || !operatorPin">{{ authBusy ? 'Checking…' : 'Unlock POS' }}</BaseButton>
        </form>
        <BaseAlert v-if="authError" class="mt-4" severity="warning" @close="authError = ''">{{ authError }}</BaseAlert>
        <p class="mt-4 text-center text-xs text-[var(--pn-text-subtle)]">Store: {{ deviceContext.storeId }} · {{ deviceContext.label }} · Device configuration revision {{ deviceConfigSnapshot?.configRevision }}</p>
      </section>
    </main>

    <main v-else class="mx-auto grid max-w-[1600px] gap-6 overflow-x-hidden px-4 py-6 lg:grid-cols-[220px_1fr] lg:px-6">
      <aside class="bg-[var(--pn-surface)] text-[var(--pn-text)] hidden rounded-2xl border border-[var(--pn-border)] p-3 shadow-sm lg:block">
        <div class="mb-3 px-3 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--pn-text-muted)]">Workspace</div>
        <nav class="space-y-1">
          <BaseButton v-for="item in navigationItems" :key="item[0]" class="flex min-h-10 w-full min-w-0 items-center justify-start gap-2 rounded-lg border border-transparent px-3 py-2 text-left text-sm text-[var(--pn-text-muted)] transition-colors hover:bg-[var(--pn-surface-muted)] hover:text-[var(--pn-text)] data-[active=true]:border-[var(--pn-border-strong)] data-[active=true]:bg-[var(--pn-selection-bg)] data-[active=true]:font-bold data-[active=true]:text-[var(--pn-selection-text)]" variant="ghost" :data-active="view === item[0]" @click="selectView(item[0])">
            {{ item[1] }}
          </BaseButton>
        </nav>
        <div class="mt-8 rounded-xl bg-[var(--pn-surface-muted)] p-3 text-xs leading-5 text-[var(--pn-text-subtle)]">
          Offline checkout writes an order and its outbox mutation together before showing success.
        </div>
      </aside>

      <section class="min-w-0">
        <div class="mb-5 flex items-end justify-between gap-4">
          <div>
            <p class="text-sm font-medium text-[var(--pn-selection-text)]">Workspace</p>
            <h1 class="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">{{ viewTitle }}</h1>
          </div>
          <BaseButton class="lg:hidden" size="sm" @click="selectView(view === 'pos' ? 'orders' : 'pos')">
            {{ view === 'pos' ? 'Order history' : 'Back to POS' }}
          </BaseButton>
        </div>

        <BaseAlert v-if="message" class="mb-4" severity="info" @close="message = ''">{{ message }}</BaseAlert>
        <BaseAlert v-if="errorMessage" class="mb-4" severity="error" @close="errorMessage = ''">{{ errorMessage }}</BaseAlert>
        <BaseAlert v-if="authError" class="mb-4" severity="warning" @close="authError = ''">{{ authError }}</BaseAlert>
        <div v-if="loading" class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-10 text-center text-sm text-[var(--pn-text-subtle)] shadow-sm">Loading local POS data…</div>

        <div v-else-if="view === 'pos'" class="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div class="min-w-0 rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-4 shadow-sm sm:p-6">
            <div class="mb-5 flex gap-2 overflow-x-auto pb-1">
              <BaseButton v-for="category in categories" :key="category" class="whitespace-nowrap" :variant="activeCategory === category ? 'primary' : 'secondary'" @click="activeCategory = category">
                {{ category }}
              </BaseButton>
            </div>
            <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <BaseButton v-for="item in visibleMenu" :key="item.id" class="min-h-28 rounded-2xl p-4 text-center uppercase tracking-wide shadow-sm transition hover:-translate-y-0.5" @click="addToCart(item)">
                <span>{{ item.name }}</span>
                <span class="mt-2 block text-sm font-normal normal-case text-[var(--pn-selection-text)]">{{ formatMoney(item.priceMinor) }}</span>
              </BaseButton>
            </div>
          </div>
          <aside class="flex min-h-[420px] min-w-0 flex-col rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-sm">
            <div class="flex items-center justify-between">
              <h2 class="text-xl font-semibold">Cart</h2>
              <span class="rounded-full bg-[var(--pn-surface-muted)] px-2.5 py-1 text-xs text-[var(--pn-text-subtle)]">{{ cart.length }} lines</span>
            </div>
            <div v-if="cart.length" class="mt-5 space-y-3">
              <div v-for="line in cart" :key="`${line.productId}-${line.note ?? ''}-${(line.options ?? []).map((option) => option.optionId).join('-')}`" class="rounded-xl bg-[var(--pn-surface-muted)] p-3">
                <div class="flex items-start justify-between gap-2 text-sm font-medium">
                  <span>{{ line.name }}<small v-if="line.options?.length" class="mt-1 block font-normal text-[var(--pn-text-subtle)]">{{ line.options.map((option) => option.name).join(' · ') }}</small><small v-if="line.note" class="mt-1 block font-normal text-[var(--pn-text-subtle)]">Note: {{ line.note }}</small></span><span>{{ formatMoney(lineTotalMinor(line)) }}</span>
                </div>
                <div class="mt-2 flex items-center gap-2 text-xs text-[var(--pn-text-subtle)]">
                  <BaseButton class="size-7 min-h-7 p-0" size="sm" aria-label="Decrease quantity" @click="changeQuantity(line.productId, -1)">−</BaseButton>
                  <span class="w-5 text-center">{{ line.quantity }}</span>
                  <BaseButton class="size-7 min-h-7 p-0" size="sm" aria-label="Increase quantity" @click="changeQuantity(line.productId, 1)">+</BaseButton>
                </div>
              </div>
            </div>
            <div v-else class="grid flex-1 place-items-center text-sm text-[var(--pn-text-muted)]">Cart is empty</div>
            <div class="mt-auto border-t border-[var(--pn-border)] pt-4">
              <div class="mb-3 flex items-center justify-between text-sm text-[var(--pn-text-subtle)]"><span>Subtotal</span><span>{{ formatMoney(cartSubtotalMinor) }}</span></div>
              <div class="mb-3 grid grid-cols-[1fr_auto] items-center gap-2 text-sm"><label for="discount">Amount discount</label><BaseInput id="discount" :model-value="discountMinor" class="w-28 text-right" type="number" min="0" :max="cartSubtotalMinor" @update:model-value="discountMinor = Number($event) || 0" /></div>
              <label class="mb-3 grid grid-cols-[1fr_auto] items-center gap-2 text-sm"><span>{{ t("ui.percentageDiscount") }}</span><PosSelect v-model="discountType" :options="discountOptions" class="w-28" /></label>
              <label v-if="discountMinor > 0 || discountType !== 'none'" class="mb-3 block text-sm"><span class="text-[var(--pn-text-muted)]">Discount reason</span><BaseInput v-model="discountReason" class="mt-1" placeholder="Required for audit" /></label>
              <label class="mb-3 block text-sm"><span class="text-[var(--pn-text-muted)]">Order note / kitchen note</span><BaseInput v-model="orderNote" type="textarea" class="mt-1" rows="2" placeholder="Optional note for this order" /></label>
              <div class="mb-3 flex items-center justify-between text-lg font-semibold"><span>Total</span><span>{{ formatMoney(cartTotalMinor) }}</span></div>
              <div class="mb-3 grid grid-cols-3 gap-2">
                <BaseButton v-for="method in ['cash', 'card', 'mixed']" :key="method" class="px-2 py-2 text-xs capitalize" :variant="paymentMethod === method ? 'primary' : 'secondary'" @click="setPaymentMethod(method as PaymentMethod)">{{ method }}</BaseButton>
              </div>
              <div v-if="paymentMethod === 'cash'" class="mb-3 grid grid-cols-2 gap-2 text-sm"><label>Cash due<BaseInput :model-value="formatMoney(cashDueMinor)" class="mt-1 text-right" disabled /></label><label>Received<BaseInput :model-value="cashReceivedMinor ?? ''" class="mt-1 text-right" type="number" min="0" @update:model-value="cashReceivedMinor = Number($event) || null" /></label><div class="col-span-2 flex justify-between rounded-lg bg-[var(--pn-success-bg)] px-3 py-2 text-[var(--pn-success)]"><span>Change</span><strong>{{ formatMoney(changeMinor) }}</strong></div></div>
              <div v-if="paymentMethod === 'mixed'" class="mb-3 grid grid-cols-2 gap-2 text-sm"><label>Cash<BaseInput :model-value="mixedCashMinor" class="mt-1 text-right" type="number" min="1" :max="cartTotalMinor - 1" @update:model-value="mixedCashMinor = Number($event) || 0" /></label><label>Card<BaseInput :model-value="formatMoney(cardDueMinor)" class="mt-1 text-right" disabled /></label></div>
              <div v-if="paymentMethod === 'card' || paymentMethod === 'mixed'" class="mb-3 rounded-lg border border-[var(--pn-border)] bg-[var(--pn-selection-bg)] p-3 text-sm text-[var(--pn-selection-text)]"><label class="flex items-start gap-2"><BaseCheckbox v-model="cardConfirmed" class="mt-0.5" /><span>I confirmed the external card terminal approved {{ formatMoney(cardDueMinor) }}.</span></label><BaseInput v-model="cardReference" class="mt-2" placeholder="Optional terminal reference" /></div>
              <BaseButton class="mb-2 w-full" variant="danger" :disabled="busy" @click="recordNoSale">No Sale / Open drawer</BaseButton>
              <BaseButton class="w-full" variant="primary" size="lg" :disabled="cart.length === 0 || busy" @click="checkout">
                {{ busy ? 'Saving locally…' : 'Checkout offline' }}
              </BaseButton>
            </div>
          </aside>
        </div>

        <div v-else-if="view === 'orders'" class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-sm">
          <div class="mb-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3"><label class="block text-sm"><span class="text-[var(--pn-text-muted)]">Search local order, item or note</span><BaseInput v-model="orderHistoryQuery" class="mt-1 w-full" placeholder="Order number or text" /></label><label class="block text-sm"><span class="text-[var(--pn-text-muted)]">Server order number</span><BaseInput v-model="orderHistoryOrderNo" class="mt-1 w-full" placeholder="Order number" @change="refreshRemoteHistoryFilter" /></label><label class="block text-sm"><span class="text-[var(--pn-text-muted)]">Server item name</span><BaseInput v-model="orderHistoryItemName" class="mt-1 w-full" placeholder="Item name" @change="refreshRemoteHistoryFilter" /></label><BaseDatePicker id="operator-order-history-range" :model-value="orderHistoryDateRangeValue" mode="range" calendar-count="single" :adapter="posDateRangeAdapter" :locale="locale" :time-zone="orderHistoryDatePickerTimeZone" aria-label="Order history date range" class="w-full" variant="outlined" @update:model-value="setOrderHistoryDateRange" /><label class="block text-sm"><span class="text-[var(--pn-text-muted)]">Status</span><PosSelect v-model="orderHistoryStatus" :options="orderStatusOptions" class="mt-1 w-full" @change="refreshRemoteHistoryFilter" /></label><label class="block text-sm"><span class="text-[var(--pn-text-muted)]">Payment</span><PosSelect v-model="orderHistoryPayment" :options="paymentOptions" class="mt-1 w-full" @change="refreshRemoteHistoryFilter" /></label><label class="block text-sm"><span class="text-[var(--pn-text-muted)]">Amount range (minor)</span><span class="mt-1 flex gap-2"><BaseInput v-model="orderHistoryMinAmount" type="number" min="0" class="w-full" placeholder="Min" @change="refreshRemoteHistoryFilter" /><BaseInput v-model="orderHistoryMaxAmount" type="number" min="0" class="w-full" placeholder="Max" @change="refreshRemoteHistoryFilter" /></span></label></div>
          <div class="mb-5 grid gap-3 sm:grid-cols-3">
            <div class="rounded-xl bg-[var(--pn-surface-muted)] p-4"><div class="text-xs text-[var(--pn-text-subtle)]">Local orders</div><div class="mt-1 text-2xl font-semibold">{{ orders.length }}</div></div>
            <div class="rounded-xl bg-[var(--pn-selection-bg)] p-4"><div class="text-xs text-[var(--pn-selection-text)]">Local sales</div><div class="mt-1 text-2xl font-semibold text-[var(--pn-selection-text)]">{{ formatMoney(totalSalesMinor) }}</div></div>
            <div class="rounded-xl bg-[var(--pn-warning-bg)] p-4"><div class="text-xs text-[var(--pn-warning)]">Pending sync</div><div class="mt-1 text-2xl font-semibold text-[var(--pn-warning)]">{{ pendingCount }}</div></div>
          </div>
          <div v-if="orders.length === 0" class="rounded-xl border border-dashed border-[var(--pn-border)] p-8 text-center text-sm text-[var(--pn-text-subtle)]">No local orders yet.</div>
          <div v-else-if="visibleOrders.length === 0" class="rounded-xl border border-dashed border-[var(--pn-border)] p-8 text-center text-sm text-[var(--pn-text-subtle)]">No matching local orders.</div>
          <BaseTable
            v-else
            :data="paginatedLocalOrders"
            :columns="localOrderColumns"
            row-key="id"
            :empty-message="t('ui.noMatchingOrders')"
            class="min-w-0"
            @row-click="(row) => openOrder(row.sourceOrder)"
          >
            <template #cell-orderNo="{ row }"><div class="font-semibold">#{{ row.orderNo }}<span class="mt-1 block text-xs font-normal text-[var(--pn-text-muted)]">{{ row.status }}</span><span v-if="row.note" class="mt-1 block max-w-xs truncate text-xs font-normal text-[var(--pn-text-subtle)]">{{ row.note }}</span></div></template>
            <template #cell-createdAt="{ value }"><span class="text-[var(--pn-text-subtle)]">{{ formatDate(String(value)) }}</span></template>
            <template #cell-paymentMethod="{ value }"><span class="capitalize text-[var(--pn-text-subtle)]">{{ value }}</span></template>
            <template #cell-totalMinor="{ value }"><span class="font-semibold">{{ formatMoney(Number(value)) }}</span></template>
            <template #cell-actions="{ row }"><div class="flex flex-wrap justify-end gap-1" @click.stop><BaseButton size="sm" variant="secondary" @click="openOrder(row.sourceOrder)">View</BaseButton><BaseButton v-if="can('orders.reprint')" size="sm" variant="secondary" @click="reprintOrder(row.sourceOrder)">Reprint</BaseButton><BaseButton v-if="row.status === 'completed' && can('orders.void')" size="sm" variant="secondary" @click="correctOrder(row.sourceOrder, 'void')">Void</BaseButton><BaseButton v-if="row.status === 'completed' && can('orders.refund')" size="sm" variant="danger" @click="correctOrder(row.sourceOrder, 'refund')">Refund</BaseButton></div></template>
          </BaseTable>
          <BasePagination v-if="visibleOrders.length" v-model="localOrderPage" v-model:page-size="localOrderPageSize" class="mt-4 border-t border-[var(--pn-border)] pt-4" :total="visibleOrders.length" :page-sizes="[10, 20, 50]" :total-label="t('ui.total')" :records-label="t('ui.ordersCount')" :rows-label="t('ui.rows')" :page-label="t('ui.page')" :pagination-label="t('ui.pagination')" :first-page-label="t('ui.firstPage')" :previous-page-label="t('ui.previous')" :next-page-label="t('ui.next')" :last-page-label="t('ui.lastPage')" @change="handleLocalOrderPagination" />
          <div v-if="operatorSession" class="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--pn-border)] pt-4"><span class="text-[var(--pn-text-muted)] text-xs">Authenticated server history is loaded in 100-row pages; local-first records remain visible even when the server is unavailable.</span><BaseButton v-if="remoteHistoryHasMore" size="sm" variant="secondary" :disabled="remoteHistoryLoading" @click="loadMoreRemoteHistory">{{ remoteHistoryLoading ? 'Loading…' : 'Load more server history' }}</BaseButton><span v-else class="text-[var(--pn-text-muted)] text-xs">All currently available server pages loaded.</span></div>
        </div>

        <div v-else-if="view === 'dashboard'" class="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-sm"><div class="text-sm text-[var(--pn-text-subtle)]">Local sales</div><div class="mt-2 text-3xl font-semibold">{{ formatMoney(totalSalesMinor) }}</div><p class="mt-2 text-xs text-[var(--pn-text-muted)]">Device-local total; server report is a later reconciliation view.</p></div>
          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-sm"><div class="text-sm text-[var(--pn-text-subtle)]">Orders on this device</div><div class="mt-2 text-3xl font-semibold">{{ orders.length }}</div><p class="mt-2 text-xs text-[var(--pn-text-muted)]">Append-only local records.</p></div>
          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-sm"><div class="text-sm text-[var(--pn-text-subtle)]">Sync queue</div><div class="mt-2 text-3xl font-semibold text-[var(--pn-warning)]">{{ pendingCount }}</div><p class="mt-2 text-xs text-[var(--pn-text-muted)]">{{ syncState }}</p></div>
          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-warning-bg)] p-5 shadow-sm"><div class="text-sm text-[var(--pn-warning)]">Sync conflicts</div><div class="mt-2 text-3xl font-semibold text-[var(--pn-warning)]">{{ conflictCount }}</div><p class="mt-2 text-xs text-[var(--pn-warning)]">Local and server versions are retained for review.</p></div>
          <div v-if="conflicts.length" class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-sm md:col-span-2 xl:col-span-3"><div class="flex flex-wrap items-start justify-between gap-3"><div><h2 class="font-semibold text-[var(--pn-warning)]">Conflict review</h2><p class="mt-1 text-sm text-[var(--pn-text-subtle)]">Both versions are retained locally. No version is silently overwritten or auto-selected.</p></div><span class="text-xs text-[var(--pn-warning)]">{{ conflicts.filter((conflict) => !conflict.resolvedAt).length }} unresolved</span></div><div class="mt-4 space-y-3"><div v-for="conflict in conflicts" :key="conflict.id" class="rounded-xl border border-[var(--pn-border)] bg-[var(--pn-warning-bg)]/60 p-4"><div class="flex flex-wrap items-center justify-between gap-2 text-sm"><span class="font-medium text-[var(--pn-text)]">Order {{ conflict.orderId }}</span><span class="text-xs text-[var(--pn-text-subtle)]">{{ conflict.reason }} · {{ new Date(conflict.detectedAt).toLocaleString('en-GB') }}</span></div><div class="mt-3 grid gap-3 text-xs sm:grid-cols-2"><div class="rounded-lg bg-[var(--pn-surface)] p-3"><div class="font-semibold text-[var(--pn-selection-text)]">Local version {{ conflict.localOrder.version }}</div><div class="mt-1 text-[var(--pn-text-subtle)]">{{ conflict.localOrder.status }} · {{ formatMoney(conflict.localOrder.totalMinor) }} · {{ conflict.localOrder.updatedAt }}</div></div><div class="rounded-lg bg-[var(--pn-surface)] p-3"><div class="font-semibold text-[var(--pn-brand-strong)]">Server version {{ conflict.remoteOrder.version }}</div><div class="mt-1 text-[var(--pn-text-subtle)]">{{ conflict.remoteOrder.status }} · {{ formatMoney(conflict.remoteOrder.totalMinor) }} · {{ conflict.remoteOrder.updatedAt }}</div></div></div><div class="mt-3 text-xs text-[var(--pn-warning)]">Resolution is intentionally deferred until an explicit reconciliation action is implemented.</div></div></div></div>
          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-sm md:col-span-2 xl:col-span-3"><h2 class="font-semibold">Workspace boundary</h2><p class="mt-2 max-w-2xl text-sm leading-6 text-[var(--pn-text-subtle)]">This dashboard reports data currently available on this device. It never presents local data as a Production reconciliation until the server sync and audit path confirms it.</p></div>
        </div>

        <div v-else-if="view === 'settings'" class="grid gap-5 lg:grid-cols-2">
          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-sm"><h2 class="font-semibold">{{ t("ui.localSyncSettings") }}</h2><p class="mt-1 text-sm text-[var(--pn-text-subtle)]">{{ t("ui.browserOnlyPreferences") }}</p><div class="mt-5 grid gap-3 sm:grid-cols-2"><label class="block text-sm"><span class="text-[var(--pn-text-muted)]">{{ t("ui.language") }}</span><PosSelect v-model="settings.language" :options="languageOptions" class="mt-1 w-full" :disabled="!can('settings.manage')" /></label><label class="block text-sm"><span class="text-[var(--pn-text-muted)]">{{ t("ui.theme") }}</span><PosSelect v-model="settings.theme" :options="themeOptions" class="mt-1 w-full" :disabled="!can('settings.manage')" /></label></div><label class="mt-4 flex items-center gap-3 text-sm"><BaseCheckbox v-model="settings.autoSync" :disabled="!can('settings.manage')" /><span>{{ t("ui.enableAutoSync") }}</span></label><label class="mt-4 block text-sm"><span class="text-[var(--pn-text-muted)]">{{ t("ui.intervalSeconds") }}</span><BaseInput :model-value="settings.syncIntervalSeconds" type="number" min="15" step="15" class="mt-1 w-full" :disabled="!can('settings.manage')" @update:model-value="settings.syncIntervalSeconds = Number($event)" /></label><div class="mt-5 flex flex-wrap gap-2"><BaseButton variant="primary" :disabled="!can('settings.manage')" @click="saveSettings">{{ t("ui.saveLocalSettings") }}</BaseButton><BaseButton variant="secondary" @click="updateApp">{{ t("ui.checkAppUpdate") }}</BaseButton><BaseButton variant="danger" :disabled="!can('settings.manage')" @click="resetLocalData">{{ t("ui.resetLocalData") }}</BaseButton></div><p class="mt-3 text-xs text-[var(--pn-text-subtle)]">{{ t("ui.resetLocalDataNotice") }}</p></div>
          <div class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-sm"><h2 class="font-semibold">{{ t("ui.printerDevice") }}</h2><p class="mt-1 text-sm text-[var(--pn-text-subtle)]">{{ t("ui.printerCapabilityNotice") }}</p><label class="mt-5 block text-sm"><span class="text-[var(--pn-text-muted)]">{{ t("ui.defaultTransport") }}</span><PosSelect v-model="settings.printTransport" :options="printTransportOptions" class="mt-1 w-full" :disabled="!can('settings.manage')" /></label><div class="mt-4 grid gap-3 sm:grid-cols-2"><label class="block text-sm"><span class="text-[var(--pn-text-muted)]">{{ t("ui.receiptRoute") }}</span><PosSelect v-model="settings.receiptTransport" :options="routeTransportOptions" class="mt-1 w-full" :disabled="!can('settings.manage')" /></label><label class="block text-sm"><span class="text-[var(--pn-text-muted)]">{{ t("ui.kitchenRoute") }}</span><PosSelect v-model="settings.kitchenTransport" :options="routeTransportOptions" class="mt-1 w-full" :disabled="!can('settings.manage')" /></label></div><label class="mt-4 block text-sm"><span class="text-[var(--pn-text-muted)]">{{ t("ui.paperWidth") }}</span><PosSelect v-model="settings.paperWidth" :options="paperWidthOptions" class="mt-1 w-full" :disabled="!can('settings.manage')" /></label><label class="mt-4 block text-sm"><span class="text-[var(--pn-text-muted)]">{{ t("ui.printMode") }}</span><PosSelect v-model="settings.printCopies" :options="printModeOptions" class="mt-1 w-full" :disabled="!can('settings.manage')" /></label><label class="mt-4 block text-sm"><span class="text-[var(--pn-text-muted)]">{{ t("ui.localBridgeEndpoint") }}</span><BaseInput v-model="settings.bridgeEndpoint" class="mt-1 w-full" :disabled="!can('settings.manage')" /></label><div class="mt-5 grid gap-2 text-sm"><div class="flex justify-between rounded-lg bg-[var(--pn-surface-muted)] px-3 py-2"><span>{{ t("ui.webSerial") }}</span><span :class="webSerialAvailable ? 'text-[var(--pn-success)]' : 'text-[var(--pn-text-muted)]'">{{ webSerialAvailable ? t("ui.available") : t("ui.unavailable") }}</span></div><div class="flex justify-between rounded-lg bg-[var(--pn-surface-muted)] px-3 py-2"><span>{{ t("ui.localBridge") }}</span><span :class="bridgeAvailable ? 'text-[var(--pn-success)]' : 'text-[var(--pn-text-muted)]'">{{ bridgeAvailable ? t("ui.available") : t("ui.unavailable") }}</span></div><div v-if="hardwareStatus" class="rounded-lg bg-[var(--pn-surface-muted)] px-3 py-2 text-xs text-[var(--pn-text-subtle)]">{{ hardwareStatus.state }} · {{ hardwareStatus.reason }} · {{ t("ui.drawer") }} {{ hardwareStatus.capabilities.cashDrawer ? t("ui.available") : t("ui.unavailable") }} · {{ t("ui.cutter") }} {{ hardwareStatus.capabilities.cutter ? t("ui.available") : t("ui.unavailable") }}</div><div class="rounded-lg bg-[var(--pn-surface-muted)] px-3 py-2 text-xs text-[var(--pn-text-subtle)]">{{ t("ui.bluetoothUsbBridgeNotice") }}: {{ hardwareTransportPath(settings.receiptTransport === 'bluetooth' || settings.kitchenTransport === 'bluetooth' ? 'bluetooth' : settings.receiptTransport === 'usb' || settings.kitchenTransport === 'usb' ? 'usb' : 'local-bridge').note }}</div></div><div class="mt-5 flex flex-wrap gap-2"><BaseButton variant="secondary" @click="checkDevices">{{ t("ui.checkCapabilities") }}</BaseButton><BaseButton variant="primary" @click="processPrintQueue">{{ t("ui.processPrintJobs") }}</BaseButton></div></div>
        </div>

        <div v-else class="rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-6 shadow-sm"><div class="rounded-xl border border-dashed border-[var(--pn-border)] bg-[var(--pn-surface-muted)] p-8 text-center text-sm text-[var(--pn-text-subtle)]">{{ viewTitle }} module is reserved for its admitted implementation phase.</div></div>
      </section>
      <div v-if="view === 'settings'" class="mt-5 rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-5 shadow-sm">
        <div class="flex flex-wrap items-start justify-between gap-3"><div><h2 class="font-semibold">Named printer profiles</h2><p class="mt-1 text-sm text-[var(--pn-text-subtle)]">Save 58mm/80mm receipt or kitchen devices and manage the local bridge lifecycle.</p></div><div class="flex flex-wrap gap-2"><BaseButton size="sm" variant="secondary" :disabled="!can('settings.manage')" @click="createPrinterProfile">Add profile</BaseButton><BaseButton size="sm" variant="secondary" :disabled="!can('settings.manage')" @click="installBridge">Install/check bridge</BaseButton></div></div>
        <div v-if="settings.printerProfiles.length === 0" class="mt-4 rounded-xl border border-dashed border-[var(--pn-border)] p-4 text-sm text-[var(--pn-text-subtle)]">No named printer profiles saved on this browser.</div>
        <div v-else class="mt-4 grid gap-3 md:grid-cols-2"><div v-for="profile in settings.printerProfiles" :key="profile.id" class="rounded-xl bg-[var(--pn-surface-muted)] p-4"><div class="flex flex-wrap items-center justify-between gap-2"><div><div class="font-medium">{{ profile.label }}</div><div class="mt-1 text-xs text-[var(--pn-text-subtle)]">{{ profile.id }} · {{ profile.transport }} · {{ profile.paperWidth }}mm<span v-if="profile.deviceAddress"> · {{ profile.deviceAddress }}</span></div></div><span class="rounded-full bg-[var(--pn-surface)] px-2 py-1 text-xs text-[var(--pn-text-subtle)]">{{ profile.enabled ? 'Enabled' : 'Disabled' }}</span></div><div class="mt-3 flex flex-wrap gap-2"><BaseButton size="sm" variant="secondary" :disabled="!can('settings.manage')" @click="pairPrinterProfile(profile)">Pair</BaseButton><BaseButton size="sm" variant="secondary" :disabled="!can('settings.manage')" @click="reconnectPrinterProfile(profile)">Reconnect</BaseButton></div></div></div>
        <div v-if="bridgeLifecycle" class="mt-4 rounded-xl bg-[var(--pn-surface-muted)] p-3 text-xs text-[var(--pn-text-subtle)]">Bridge: {{ bridgeLifecycle.state }} · version {{ bridgeLifecycle.version ?? 'unknown' }} · {{ bridgeLifecycle.message }}</div>
      </div>
    </main>
    <div v-if="operatorUnlocked && selectedOrder" class="fixed inset-0 z-20 grid place-items-center bg-[var(--pn-surface)]/40 p-4" @click.self="selectedOrder = null">
      <div class="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-[var(--pn-surface)] p-6 shadow-xl">
        <div class="flex items-start justify-between gap-4"><div><h2 class="text-xl font-semibold">Order #{{ selectedOrder.orderNo ?? selectedOrder.id.slice(0, 8) }}</h2><p class="mt-1 text-sm text-[var(--pn-text-subtle)]">{{ formatDate(selectedOrder.createdAt) }} · {{ selectedOrder.status }}<span v-if="selectedOrderLoading"> · loading server detail…</span></p></div><BaseButton variant="ghost" aria-label="Close order details" @click="selectedOrder = null">×</BaseButton></div>
        <div class="mt-5 space-y-2"> <div v-for="line in selectedOrder.lines" :key="`${line.productId}-${line.note ?? ''}-${(line.options ?? []).map((option) => option.optionId).join('-')}`" class="rounded-xl bg-[var(--pn-surface-muted)] p-3 text-sm"><div class="flex justify-between gap-3"><span>{{ line.quantity }} × {{ line.name }}</span><span>{{ formatMoney(lineTotalMinor(line) * line.quantity) }}</span></div><div v-if="line.options?.length" class="mt-1 text-xs text-[var(--pn-text-subtle)]">{{ line.options.map((option) => option.name).join(' · ') }}</div><div v-if="line.note" class="mt-1 text-xs text-[var(--pn-text-subtle)]">Note: {{ line.note }}</div></div></div>
        <dl class="mt-5 divide-y divide-slate-100 rounded-xl border border-[var(--pn-border)] text-sm"><div class="flex justify-between px-4 py-3"><dt class="text-[var(--pn-text-subtle)]">Subtotal</dt><dd>{{ formatMoney(selectedOrder.subtotalMinor) }}</dd></div><div class="flex justify-between px-4 py-3"><dt class="text-[var(--pn-text-subtle)]">Discount</dt><dd>{{ formatMoney(selectedOrder.discountMinor) }}<span v-if="selectedOrder.discountType && selectedOrder.discountType !== 'none'" class="ml-2 text-xs text-[var(--pn-text-subtle)]">({{ selectedOrder.discountType }})</span></dd></div><div v-if="selectedOrder.discountReason" class="flex justify-between px-4 py-3"><dt class="text-[var(--pn-text-subtle)]">Discount reason</dt><dd>{{ selectedOrder.discountReason }}</dd></div><div class="flex justify-between px-4 py-3 font-semibold"><dt>Total</dt><dd>{{ formatMoney(selectedOrder.totalMinor) }}</dd></div></dl>
        <p v-if="selectedOrder.note" class="mt-4 rounded-xl bg-[var(--pn-warning-bg)] p-3 text-sm text-[var(--pn-warning)]">Order note: {{ selectedOrder.note }}</p>
        <div v-if="selectedOrderEvents.length" class="mt-4 rounded-xl border border-[var(--pn-border)] p-4"><h3 class="font-semibold">Correction history</h3><div class="mt-3 space-y-2"><div v-for="event in selectedOrderEvents" :key="event.id" class="rounded-lg bg-[var(--pn-surface-muted)] p-3 text-sm"><div class="flex justify-between gap-3"><span class="font-medium capitalize">{{ event.type }}</span><span class="text-xs text-[var(--pn-text-subtle)]">{{ formatDate(event.occurredAt) }}</span></div><div class="mt-1 text-xs text-[var(--pn-text-subtle)]">{{ event.actorId }}<span v-if="event.refundMethod"> · {{ event.refundMethod }} refund</span><span v-if="event.reason"> · {{ event.reason }}</span></div></div></div></div>
      </div>
    </div>
    <div v-if="operatorUnlocked && customizingItem" class="fixed inset-0 z-20 grid place-items-center bg-[var(--pn-surface)]/40 p-4" @click.self="customizingItem = null">
      <div class="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-[var(--pn-surface)] p-6 shadow-xl">
        <div class="flex items-start justify-between gap-4"><div><h2 class="text-xl font-semibold">Customize {{ customizingItem.name }}</h2><p class="mt-1 text-sm text-[var(--pn-text-subtle)]">Choose filling, sauce and extras before adding it.</p></div><BaseButton variant="ghost" aria-label="Close item customization" @click="customizingItem = null">×</BaseButton></div>
        <div class="mt-5 space-y-4">
          <div v-for="group in customizingItem.optionGroups" :key="group.id" class="rounded-xl bg-[var(--pn-surface-muted)] p-4"><div class="mb-2 flex justify-between text-sm font-semibold"><span>{{ group.name }}</span><span class="flex gap-2 text-xs"><span v-if="group.required" class="text-[var(--pn-danger)]">Required</span><span v-else class="text-[var(--pn-text-subtle)]">Optional</span><span v-if="group.multiple" class="text-[var(--pn-text-subtle)]">Multiple</span><span v-else class="text-[var(--pn-text-subtle)]">One</span></span></div><div class="grid gap-2 sm:grid-cols-2"><label v-for="option in group.options.filter((candidate) => candidate.active)" :key="option.id" class="flex items-center justify-between rounded-lg bg-[var(--pn-surface)] px-3 py-2 text-sm"><span class="flex items-center gap-2"><BaseCheckbox :type="group.multiple ? 'checkbox' : 'radio'" :name="`${customizingItem.id}-${group.id}`" :model-value="(customOptions[group.id] ?? []).includes(option.id)" :aria-label="option.name" @change="customOptions[group.id] = group.multiple ? ((customOptions[group.id] ?? []).includes(option.id) ? (customOptions[group.id] ?? []).filter((id) => id !== option.id) : [...(customOptions[group.id] ?? []), option.id]) : [option.id]" />{{ option.name }}</span><span class="text-xs text-[var(--pn-text-subtle)]">{{ option.priceMinor ? formatMoney(option.priceMinor) : 'Included' }}</span></label></div></div>
        </div>
        <label class="mt-4 block text-sm"><span class="text-[var(--pn-text-muted)]">Item note</span><BaseInput v-model="itemNote" type="textarea" rows="2" class="mt-1 w-full" placeholder="Optional kitchen note" /></label>
        <div class="mt-5 flex justify-end gap-2"><BaseButton variant="secondary" @click="customizingItem = null">Cancel</BaseButton><BaseButton variant="primary" @click="confirmCustomization">Add to cart</BaseButton></div>
      </div>
    </div>
    <PwaUpdateDialog
      v-model:visible="pwaUpdateVisible"
      :busy="pwaUpdateBusy"
      :title="t('ui.updateAvailable')"
      :message="t('ui.updateMessage')"
      :later-label="t('ui.updateLater')"
      :update-label="t('ui.updateNow')"
      @update-now="applyPwaUpdate"
    />
  </div>
</template>
